import { useState, useEffect } from 'react';
import { ScryfallCard } from '@/types/scryfall';
import { useScryfall } from '@/hooks/useScryfall';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Eye, Plus, DicesIcon } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '@/hooks/useToast';
import { CardDetailsDialog } from './CardDetailsDialog';

const RandomCardSuggestions = ({ addCard }: { addCard: (card: ScryfallCard) => void }) => {
  const [numCards] = useState(3);
  const [cards, setCards] = useState<ScryfallCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const [detailsCard, setDetailsCard] = useState<ScryfallCard | null>(null);
  const scryfall = useScryfall();

  useEffect(() => {
    const fetchRandomCards = async () => {
      setLoading(true);
      setError(null);
      try {
        const results: ScryfallCard[] = [];
        for (let i = 0; i < numCards; i++) {
          const card = await scryfall.getRandomCard();
          results.push(card);
          await new Promise(resolve => setTimeout(resolve, 50)); // 50ms delay
        }
        setCards(results);
      } catch {
        setError('Failed to fetch random cards. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we don't have cards yet and haven't encountered an error
    if (cards.length === 0 && !error) {
      fetchRandomCards();
    }
  }, [scryfall, numCards, cards.length, error]);

  const handleAddCard = (card: ScryfallCard) => {
    addCard(card);
    toast({
      title: "Card added",
      description: `${card.name} added to surveilled cards.`,
    });
  };

  const getCardImageUrl = (card: ScryfallCard): string => {
    if (card.image_uris?.normal) {
      return card.image_uris.normal;
    }
    if (card.card_faces?.[0]?.image_uris?.normal) {
      return card.card_faces[0].image_uris.normal;
    }
    return '';
  };

  if (loading) {
    return (
      <div className="hidden md:block">
        <div className="mt-8">
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <DicesIcon className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Feeling Lucky?</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-1 ml-8">Discover new cards to add to your collection.</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {Array.from({ length: numCards }).map((_, i) => (
              <Card key={i} className="overflow-hidden border-border/50">
                <div className="aspect-[5/7] relative">
                  <Skeleton className="w-full h-full" />
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="hidden md:block mt-8">
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <DicesIcon className="h-5 w-5 text-primary" />
            <h3 className="font-semibold text-foreground">Feeling Lucky?</h3>
          </div>
          <p className="text-sm text-muted-foreground mt-1 ml-8">Discover new cards to add to your collection.</p>
        </div>
        <Card className="border-dashed">
          <CardContent className="py-8 px-4 text-center">
            <div className="max-w-sm mx-auto space-y-4">
              <p className="text-destructive text-sm">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setError(null);
                  setCards([]);
                }}
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="hidden md:block">
        <div className="mt-8">
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <DicesIcon className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-foreground">Feeling Lucky?</h3>
            </div>
            <p className="text-sm text-muted-foreground mt-1 ml-8">Discover new cards to add to your collection.</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {cards.map(card => (
              <Card key={card.id} className="group overflow-hidden border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 rounded-lg">
                <div className="aspect-[5/7] relative overflow-hidden rounded-t-lg cursor-pointer" onClick={() => setDetailsCard(card)}>
                  {getCardImageUrl(card) ? (
                    <img
                      src={getCardImageUrl(card)}
                      alt={card.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center">
                      <Eye className="h-12 w-12 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => { e.stopPropagation(); handleAddCard(card); }}
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-300 opacity-100 lg:opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 bg-background/80 hover:bg-background`}
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
      {detailsCard && (
        <CardDetailsDialog
          card={detailsCard}
          open={!!detailsCard}
          onOpenChange={(open) => !open && setDetailsCard(null)}
          onAddToList={() => handleAddCard(detailsCard)}
        />
      )}
    </>
  );
};

export default RandomCardSuggestions;
